import { defer, EMPTY, expand, finalize, from, map, Observable, reduce, share } from "rxjs"
import { BybitRestClient, BybitWebsocketClient } from "../../../core/market"
import { createStringNumberMap, mergeStringNumberMaps, parseFloatSafe } from "../../../core/func"
import { APIResponseV3WithTime, GetClosedPnLParamsV5, isWsAccountOrderEventV5, isWsWalletEventV5, WalletBalanceV5 } from "libs/bybit-api/src"

const requestMap = new Map<BybitRestClient, Observable<APIResponseV3WithTime<{ list: WalletBalanceV5[] }>>>()

export class BybitBalanceRestProvider {

  constructor(private rest: BybitRestClient) {}

  private createBalanceRequest() {
    let req = requestMap.get(this.rest)
    if (!req) {
      req = from(this.rest.getWalletBalance({accountType: 'UNIFIED'})).pipe(
        finalize(() => requestMap.delete(this.rest)),
        share()
      )
      requestMap.set(this.rest, req)
    }
    return req
  }

  /**
   * @deprecated
   */
  requestBalance() {
    return this.createBalanceRequest().pipe(
      map(data => parseFloatSafe(data.result.list[0].totalWalletBalance)),
    )
  }

  requestBalances() {
    return this.createBalanceRequest().pipe(
      map(data => createStringNumberMap(data.result.list[0].coin, 'coin', 'walletBalance'))
    )
  }

  /**
   * @deprecated
   */
  requestPnl(startTime: number) {
    const params: GetClosedPnLParamsV5 = {
      category: 'linear',
      startTime: startTime,
      limit: 100,
    }
    const req = defer(() => this.rest.getClosedPnL(params))
    return req.pipe(
      expand(data => {
        if (data.result.nextPageCursor && params.limit && data.result.list.length >= params.limit) {
          params.cursor = data.result.nextPageCursor
          return req
        }
        return EMPTY
      }),
      map(data => data.result.list),
      map(data => data.reduce<number>((sum, row) => sum + parseFloatSafe(row.closedPnl), 0)),
      reduce((acc, n) => acc + n, 0),
    )
  }

  requestPnls(startTime: number) {
    const params: GetClosedPnLParamsV5 = {
      category: 'linear',
      startTime: startTime,
      limit: 100,
    }
    const req = defer(() => this.rest.getClosedPnL(params))
    return req.pipe(
      expand(data => {
        if (data.result.nextPageCursor && params.limit && data.result.list.length >= params.limit) {
          params.cursor = data.result.nextPageCursor
          return req
        }
        return EMPTY
      }),
      map(data => createStringNumberMap(data.result.list, 'symbol', 'closedPnl')),
      reduce((acc, data) => mergeStringNumberMaps(acc, data), {} as Record<string, number>)
    )
  }

}

export class BybitBalanceWebsocketProvider {

  constructor(private ws: BybitWebsocketClient) {}

  /**
   * @deprecated
   */
  realtimeBalance() {
    return new Observable<number>(subscriber => {
      const f = (data: any) => {
        if (isWsWalletEventV5(data) && data.data?.length) {
          subscriber.next(parseFloatSafe(data.data[0].totalWalletBalance))
        }
      }

      this.ws.on('update', f)
      return () => this.ws.off('update', f)
    })
  }

  realtimeBalances() {
    return new Observable<Record<string, number>>(subscriber => {
      const f = (data: any) => {
        if (isWsWalletEventV5(data) && data.data?.length) {
          data.data.forEach(wallet => subscriber.next(createStringNumberMap(wallet.coin, 'coin', 'walletBalance')))
        }
      }

      this.ws.on('update', f)
      return () => this.ws.off('update', f)
    })
  }

  realtimePnlChange() {
    return new Observable<Record<string, number>>(subscriber => {
      const f = (data: any) => {
        if (isWsAccountOrderEventV5(data) && data.data?.length) {
          subscriber.next(createStringNumberMap(data.data.filter(order => order.category == 'linear'), 'symbol', 'closedPnl'))
        }
      }

      this.ws.on('update', f)
      return () => this.ws.off('update', f)
    })
  }

}
